<template>
  <section id="cart" v-clickout="cartClose" class="relative">
    <button
      @click.prevent="toggelCartDetail()"
      class="flex justify-center py-2 px-2 md:px-4 border border-primary-color shadow-sm text-xs md:text-sm lg:text-sm font-medium text-primary-color bg-white rounded-full hover:bg-primary-default/50 items-center"
    >
      <ShoppingCartIcon
        class="h-5 w-5 flex-shrink-0 text-primary-color mr-1"
        aria-hidden="true"
        :class="{ 'tada animated': animated }"
      />
      <span
        class="text-xxs rounded-full bg-red-600 transform -translate-x-2 -translate-y-1/2 p-1 h-4 w-4 flex items-center justify-center text-white"
        v-if="shoppingCart.items.length > 0"
      >
        {{ totalItems }}
      </span>
      Bestellijst
    </button>
    <div
      v-if="cartOpenStatus"
      id="cartOrder"
      class="text-black flex flex-col bg-white fixed md:absolute top-0 md:top-12 right-0 w-1/3 z-50 shadow rounded-xl p-4 mx-auto w-full md:w-96 h-screen md:h-auto overflow-y-scroll md:overflow-visible"
    >
      <div class="ml-4 flex flex-shrink-0 relative">
        <button
          @click.prevent="cartClose"
          type="button"
          class="inline-flex rounded-md bg-white text-secondary-default hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 absolute right-1"
        >
          <span class="sr-only">Close</span>
          <XMarkIcon class="h-6 w-6 pds-closeIcon" aria-hidden="true" />
        </button>
      </div>
      <template v-if="shoppingCart.items.length === 0">
        <span class="italic text-pdsPrimaryColor">Er zijn geen producten in uw bestellijst.</span>
      </template>
      <template v-if="shoppingCart.items.length > 0">
        <div class="text-2xl text-talternative-color mb-4 font-bold">Bestelling</div>
        <a
          class="text-primary-color font-bold cursor-pointer mr-4 mb-4 self-end"
          @click="clearShoppingCart(shoppingCart)"
        >
          Alles verwijderen
        </a>
        <div class="mx-4">
          <v-order
            v-for="(order, index) in shoppingCart.items"
            :key="index"
            :class="{ 'fadeInRight animated': getAnimationStatus(index) }"
            :data="order"
            @remove-order="removeOrder"
          />
        </div>
        <div class="gap-6 grid md:flex grid-cols-2 md:flex-row justify-center">
          <button
            @click.prevent="$router.push('/catalog')"
            class="flex justify-center py-2 px-2 md:px-4 text-xs md:text-base font-medium text-pdsPrimaryColor border border-primary-default items-center bg-white rounded-full"
          >
            <span class="">Naar catalogus</span>
          </button>
          <button
            @click.prevent="submitOrder"
            class="flex justify-center py-2 px-2 md:px-4 text-xs md:text-base font-medium text-white items-center bg-cta rounded-full"
          >
            <span class="">Naar bestellijst</span>
          </button>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch, inject } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { shoppingCart$, removeFromShoppingCart, clearShoppingCart, currentUserIdentity$ } from '@/services';
import { map, distinctUntilChanged } from 'rxjs/operators';
import VOrder from './Order';
import { ShoppingCartIcon, XMarkIcon } from '@heroicons/vue/20/solid';

export default {
  name: 'VCart',
  components: {
    ShoppingCartIcon,
    VOrder,
    XMarkIcon
  },
  setup() {
    const $gtm = inject('$gtm');
    const store = useStore();
    const router = useRouter();
    const shoppingCart = ref(null);
    const user = ref(null);
    const animated = ref(null);
    const orderAnimated = ref(null);
    const lastIndex = ref(null);
    const onLoad = ref(true);

    const patientDetails = computed(() => store.getters['patient/getPatientGegevens']);
    const cartOpenStatus = computed(() => store.getters['cart/getCartOpenStatus']);
    const orderClicked = computed(() => store.getters['cart/getOrderClicked']);
    const setCartOpenStatus = (value) => store.commit('cart/setCartOpenStatus', value);
    const setOrderClicked = (value) => store.commit('cart/setOrderClicked', value);
    const totalItems = computed(() => {
      return shoppingCart.value && shoppingCart.value.items.length ? shoppingCart.value.items.length : 0;
    });

    shoppingCart$.subscribe((value) => {
      shoppingCart.value = value;
    });

    currentUserIdentity$.subscribe((value) => {
      user.value = value;
    });

    const animateShoppingCartIcon = () => {
      animated.value = true;
      setTimeout(() => {
        animated.value = false;
        orderAnimated.value = false;
        lastIndex.value = null;
      }, 3000);
    };

    shoppingCart$
      .pipe(
        map((m) => (m ? m.items.length : 0)),
        distinctUntilChanged()
      )
      .subscribe();

    // Watch for changes in shoppingCart.value
    watch(
      () => shoppingCart.value,
      (newValue, oldValue) => {
        if (onLoad.value) {
          window.scrollTo(0, 0);
          onLoad.value = false;
          return;
        }
        if (newValue && newValue.items.length > 0 && JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          animateShoppingCartIcon();
        }
      },
      { deep: true } // Ensure it watches nested changes (e.g., items inside shoppingCart)
    );

    const removeOrder = (cartId) => {
      const item = shoppingCart.value.items.find((item) => {
        return item.cartId === cartId;
      });
      const index = shoppingCart.value.items.indexOf(item);
      if (index >= 0) {
        shoppingCart.value.items.splice(index, 1);
      }
      removeFromShoppingCart(shoppingCart, cartId);
    };

    const cartClose = () => {
      if (orderClicked.value) {
        setOrderClicked(false);
        return;
      }
      setCartOpenStatus(false);
    };

    const toggelCartDetail = () => {
      return cartOpenStatus.value ? setCartOpenStatus(false) : setCartOpenStatus(true);
    };

    const getAnimationStatus = (index) => {
      return index === lastIndex.value && orderAnimated.value;
    };

    const submitOrder = () => {
      setCartOpenStatus(false);
      shoppingCart.value.items.forEach((item) => {
        $gtm.push({
          event: 'ButtonClick',
          event_category: 'Product cart',
          event_action: 'Bestellen',
          event_label: `${item.productId} ${item.productTitle}`,
          event_value: String(item.quantity)
        });
      });
      if (router.currentRoute.value.meta.flow === 'replenishment') {
        router.push('/magazijnaanvullenBestellijst');
      } else {
        router.push('/bestellijst');
      }
    };

    return {
      patientDetails,
      cartOpenStatus,
      orderClicked,
      setCartOpenStatus,
      setOrderClicked,
      shoppingCart,
      user,
      animated,
      orderAnimated,
      lastIndex,
      totalItems,
      removeOrder,
      clearShoppingCart,
      cartClose,
      toggelCartDetail,
      getAnimationStatus,
      submitOrder
    };
  }
};
</script>
